import React, { useMemo } from "react";
import { Box, Stack, Typography, Paper } from "@mui/material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Link as RouterLink } from "react-router-dom";
import { isLiveStatus } from "./dashboardChips";

/**
 * Delivery exceptions — the things an admin has to act on, each linked to the screen that fixes it.
 *
 * The staffing and seat signals are derived from the capacity report, so they cover every group
 * rather than the handful the table below shows; `weakTopics` is a count from the topic-cohort
 * report. Renders nothing when there is nothing to act on: an empty exception strip is noise, and a
 * permanently-present "0 problems" tile trains people to ignore the row.
 */

const SIGNALS = [
  {
    key: "noLead",
    label: "without a lead tutor",
    to: "/admin/resource-allocation",
    Icon: PersonOffIcon,
    bg: "#fef3c7",
    fg: "#92400e",
    test: (r) => isLiveStatus(r.status) && !(r.assigned_tutors || []).length,
  },
  {
    // A group can have a lead and still deliver nothing in a subject: since staffing became
    // subject-wise, "who teaches Maths here?" is a separate question from "does it have a lead?".
    key: "uncoveredSubjects",
    label: "with a subject nobody teaches",
    to: "/admin/resource-allocation",
    Icon: MenuBookIcon,
    bg: "#ffedd5",
    fg: "#9a3412",
    test: (r) => isLiveStatus(r.status) && (r.uncovered_subjects || []).length > 0,
  },
  {
    key: "full",
    label: "full — no seats left",
    to: "/admin/analytics/batch-capacity",
    Icon: EventBusyIcon,
    bg: "#fee2e2",
    fg: "#991b1b",
    test: (r) => isLiveStatus(r.status) && r.capacity != null && (r.remaining_seats ?? 0) <= 0,
  },
  {
    key: "noCapacity",
    label: "with no seat limit set",
    to: "/admin/course-list",
    Icon: HelpOutlineIcon,
    bg: "#e0e7ff",
    fg: "#3730a3",
    test: (r) => isLiveStatus(r.status) && r.capacity == null,
  },
];

export default function DeliveryAttentionStrip({ capacity, weakTopics }) {
  const items = useMemo(() => {
    const rows = capacity
      ? SIGNALS
        .map((s) => ({ ...s, count: capacity.filter(s.test).length, unit: "group" }))
        .filter((s) => s.count > 0)
      : [];

    // Not a capacity signal: a topic the student body is failing is a class nobody has scheduled
    // yet. Same "act on it" shape, so it belongs in the same row rather than a panel of its own.
    if (weakTopics > 0) {
      rows.push({
        key: "weakTopics",
        label: "with enough students to fill a revision class",
        to: "/admin/analytics/weak-topics",
        Icon: TrendingDownIcon,
        bg: "#ede9fe",
        fg: "#5b21b6",
        count: weakTopics,
        unit: "topic",
      });
    }

    return rows;
  }, [capacity, weakTopics]);

  if (items.length === 0) return null;

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
      {items.map(({ key, label, to, Icon, bg, fg, count, unit }) => (
        <Paper
          key={key}
          component={RouterLink}
          to={to}
          sx={{
            flex: 1,
            p: 1.5,
            borderRadius: 3,
            border: "1px solid #eef0f4",
            boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0,0,0,0.08)" },
          }}
        >
          <Box
            sx={{
              width: 40, height: 40, borderRadius: "50%", bgcolor: bg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <Icon sx={{ color: fg, fontSize: 21 }} />
          </Box>
          <Typography variant="body2" sx={{ color: "#111827", fontWeight: 600 }}>
            <Box component="span" sx={{ fontWeight: 800 }}>
              {count} {count === 1 ? unit : `${unit}s`}
            </Box>{" "}
            {label}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
}
